import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { ActionRole, ButtonType, Level, ObjectPropertyFormat } from '@newroom-connect/library/enums';
import { IAPIListResponseMeta, IFileHydrated, IFileUploadItem, IListConfigurationColumn, IListItem, IListItemClickedEvent, IListOptions, ListItemType } from '@newroom-connect/library/interfaces';
import { FileListService, ListService } from '@newroom-connect/library/services';
import { IsNonEmptyArrayPipe, IsEmptyArrayPipe } from '@newroom-connect/library/pipes';

import { FileExplorerComponent } from '../../file-explorer/file-explorer.component';
import { SearchComponent } from '../../../search/search.component';
import { ListComponent } from '../../../list/list.component';

@Component({
  selector: 'nrc-file-library-modal-search',
  templateUrl: './file-library-modal-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    SearchComponent,
    ListComponent,
    FileExplorerComponent,
    IsNonEmptyArrayPipe,
    IsEmptyArrayPipe
  ]
})
export class FileLibraryModalSearchComponent {
  @Input() public fileListItemsSig = signal<IListItem<IFileHydrated>[]>([]);
  @Input() public metaSig = signal<IAPIListResponseMeta | undefined>(undefined);
  @Input() public isFileListLoading?: boolean;

  @Output() public showFileLibraryEvent = new EventEmitter<void>();
  @Output() public uploaderFilesChanged = new EventEmitter<IFileUploadItem[]>();
  @Output() public fileListEvent = new EventEmitter<IListOptions>();
  @Output() public fileSelectedEvent = new EventEmitter<IListItemClickedEvent<IListItem<IFileHydrated>>>();
  @Output() public fileSearchEvent = new EventEmitter<string>();
  @Output() public actionButtonEvent = new EventEmitter<void>();

  public columnsSig = signal<IListConfigurationColumn[]>(FileListService.generateGridLayoutListConfiguration({ enableActionButtons: true }));

  public buttonType = ButtonType;
  public actionRole = ActionRole;
  public level = Level;
  public objectPropertyFormat = ObjectPropertyFormat;
  public listItemType = ListItemType;

  private searchTerm = signal<string>('');

  private readonly listService = inject(ListService);

  /**
   * Handle list options change from the list component's paginator and emit the event.
   *
   * @param options - The new list options with pagination info.
   */
  public handleListOptions(options: IListOptions): void {
    // Update the last list options in the service
    // Get the current list options and update the search term
    const listOptions = {
      ...options
    };

    if (this.searchTerm()) {
      listOptions.search = this.searchTerm();
    }

    // Forward the event to parent components
    this.fileListEvent.emit(listOptions);
  }

  /**
   * Handle search events from the search component.
   *
   * @param searchTerm - The search term entered by the user.
   */
  public handleSearch(searchTerm: string): void {
    this.searchTerm.set(searchTerm);

    // Retrieve current listOptions from the service
    const currentOptions = this.listService.lastListOptions$.getValue();

    // Build clean list options
    const listOptions: IListOptions = {
      pagination: {
        ...currentOptions.pagination,
        page: 1
      }
    };

    // Only add search if there's a search term
    if (searchTerm) {
      listOptions.search = searchTerm;
    }

    // Add sort if it exists
    if (currentOptions.sort) {
      listOptions.sort = currentOptions.sort;
    }

    // Update service and emit events
    this.listService.lastListOptions$.next(listOptions);
    this.fileListEvent.emit(listOptions);
  }
}
